import { FamilyNode } from '../screens/App/FamilyMember/FamilyTree/types'

export enum Severity {
  error = 'error',
  info = 'info',
  success = 'success',
  warning = 'warning'
}

export type Message = {
  severity: Severity
  message: string
  eventType?: MessageType
};

export enum MessageType {
  delete = 'delete',
  create = 'create',
  update = 'update',
  upload = 'upload',
  load = 'load'
}

export enum PreviewFormat {
  square = 'square',
  origin = 'origin',
}

export enum Allergy {
  nuts = 'nuts',
  lactose = 'lactose',
  sesame = 'sesame',
  fish = 'fish',
  eggs = 'eggs',
  gluten = 'gluten',
  mustard = 'mustard',
  celery = 'celery',
  soy = 'soy',
}

export enum DietType {
  vegetarian = 'vegetarian',
  vegan = 'vegan',
  custom = 'custom',
}

export enum RelationType {
  partnership = 'partnership',
  marriage = 'marriage',
  parentChild = 'parentChild',
  adoptedParentChild = 'adoptedParentChild',
  custom = 'custom',
}

export enum Title {
  prof = 'prof',
  dr = 'dr',
  profDr = 'profDr',
}

export enum UserRole {
  user = 'user',
  organizer = 'organizer', // dürfen das event erstellen / bearbeiten, in dem sie Organisator sind
  jury = 'jury', // dürfen die Skatlisten bearbeiten / erstellen / löschen
  admin = 'admin', // dürfen alles sehen, bearbeiten, erstellen, löschen
}

export enum PermissionType {
  read = 'read',
  update = 'update',
  create = 'create',
  delete = 'delete',
}

export type UserPermissions = {
  events: PermissionType[]
  members: PermissionType[]
  relations: PermissionType[]
  eventResults: PermissionType[]
  user: PermissionType[]
  eventLocations: PermissionType[]
  eventMember: PermissionType[]
  eventPriceCategories: PermissionType[]
}

export type CustomDateObject = {
  day?: number
  month?: number // Januar = 1
  year?: number // vierstellig (>999)
}

export interface User {
  userId?: string
  login: string
  userRole?: UserRole
  memberId?: string
}

export enum Gender {
  male = 'male',
  female = 'female',
  diverse = 'diverse',
}

export const enum Generation {
  yellow = 'yellow',
  orange = 'orange',
  green = 'green',
  blue = 'blue',
  red = 'red',
  violet = 'violet',
}

export interface Member {
  originId: string
  id?: string
  title?: Title
  gender?: Gender
  firstname: string
  lastname: string
  middlenames?: string
  birthname?: string
  nickname?: string
  generation?: Generation
  address?: string
  zip?: string
  city?: string
  coordinates?: string
  country?: string
  email?: string
  phone?: string
  dietType?: DietType
  allergies: Allergy[]
  dead: boolean
  birthday: CustomDateObject
  deathday: CustomDateObject
  placeOfBirth?: string
  placeOfDeath?: string
  profileImageUrl?: string
  showDetails: boolean
  handicap?: boolean
}

export interface EventLocation {
  id?: string
  name: string
  description?: string
  signature?: string
  coordinates?: string
  linkUrl?: string
  caption?: string
  imageUrls?: string[]
}

export interface Event {
  id?: string
  title: string
  description?: string
  signature?: string
  locationId?: string // EventLocation
  organizer: string[] // Member
  eventFrom?: CustomDateObject
  eventTo?: CustomDateObject
  registrationUntil?: CustomDateObject
  imageUrls?: string[]
  tablesClosed?: boolean
  displayResults?: boolean
}

export interface EventPriceCategory {
  id?: string
  eventId: string
  defaultCount: number
  title: string
  description?: string
  price?: number
  ageFrom?: number
  ageTo?: number
}

export interface Tag { // vordefiniert alle Member / Events / Locations > bei Neuanlage von Membern / Events / Locations auch Tag mit anlegen
  id?: string
  label: string
}

export interface EventMedia {
  id: string
  mediaUrl: string
  mimeType: string
  eventId?: string
  date: CustomDateObject
  tags: string[] // mediaTagIds
}

export interface BookedEventPriceCategory {
  eventPriceCategoryId: string // EventPriceCategoryId
  quantity: number // default 1
  notes?: string // Achtung! Kann mehr Text enthalten.
}

export interface EventMember {
  id?: string
  registeredByMemberId: string // wer hat diesen Member registriert (jeder darf nur sich und die, die er angemeldet hat bearbeiten)
  eventId: string
  memberId: string
  participate: boolean
  gamer: boolean
  from: CustomDateObject
  to: CustomDateObject
  eventPriceCategories: BookedEventPriceCategory[]
  notes?: string // Achtung! Kann mehr Text enthalten.
}

export enum Game {
  eichel = 'eichel',
  gruen = 'gruen',
  rot = 'rot',
  schell = 'schell',
  grand = 'grand',
  zero = 'zero',
  eingemischt = 'eingemischt'
}

export enum GameProperty {
  einfach = 'einfach',
  schneider = 'schneider',
  schwarz = 'schwarz',
  hand = 'hand',
  handSchneider = 'handSchneider',
  handSchwarz = 'handSchwarz',
  handSchneiderAngesagt = 'handSchneiderAngesagt',
  handSchneiderAngesagtSchwarz = 'handSchneiderAngesagtSchwarz',
  handSchwarzAngesagt = 'handSchwarzAngesagt',
  handOuvert = 'handOuvert',
  ouvert = 'ouvert',
}

export enum MitOhne {
  mit = 'mit',
  ohne = 'ohne',
}

export interface Round {
  id?: string
  gameValue?: number
  player?: string
  won?: boolean
  game?: Game
  mitOhne?: MitOhne
  factor?: number
  hand?: boolean
  gameProperty?: GameProperty
  results: {
    [key: string]: number // Spieler mit aufsummierten Ergebnis
  }
}

export interface Games {
  [key: number]: Round
}

export interface EventResult {
  id?: string
  eventId: string
  table: number
  session: number
  players: string[]
  games: Games
  results: {
    [key: string]: { // spieler mit Ergebnissen
      sessionResult: number
      wonOffense: number
      lostOffense: number
      lostDefense: number
      offensePoints: number
      defensePoints: number
      completeResult: number
    }
  }
}

export interface EventResultObject {
  [key: string]: EventResult[] // an der Stelle eventId
}

export interface Relation {
  id: string
  parent: string // memberId
  child: string // memberId
  relationType: RelationType
  from: CustomDateObject
  to: CustomDateObject
  notes: string
}

export interface BiographyPart {
  date: CustomDateObject
  title: string
  description?: string
  image?: string
}

export interface Geography {
  [key: string]: string[]
}

export interface Biography {
  [key: string]: BiographyPart[]
}

export interface EternalMemberPoints {
  memberId: string
  participations: number
  points: number
}

export interface Participation {
  year: number
  male: number
  female: number
}

export interface MemberRound {
  year: number
  first: number
  second: number
}

export interface Record {
  memberId: string
  count?: number
}

export interface GameRecord {
  game: string
  count: number
}

export interface SingleMemberStatistic {
  bestGame: number
  worstGame: number
  longestWinningStreak: number
  totalGameWon: number
  totalGameLoose: number
  totalMixedIn: number
  totalRoundWon: number
  totalRoundLoose: number
  tournamentsWon: number
  bestTournament: number
  worstTournament: number
  tableOfThree: number
  tableOfFour: number
  rounds: MemberRound[]
  gameStatistics: GameStatistics
}

export interface SingleEventStatistic {
  bestGame?: Record
  worstGame?: Record
  youngestGamer?: Record
  oldestGamer?: Record
  men?: number
  women?: number
  gameStatistics: GameStatistics
  averageGamesPerPlayer: number
  totalGames: number
  mostPlayedGame: GameRecord
  biggestSessionImprovement: Record
  worstSessionDrop: Record
  mostConsistentPlayer: Record
}

export interface MemberStatistic {
  [key: string]: SingleMemberStatistic
}

export interface StarStatistic {
  [key: string]: number
}

export interface EventStatistic {
  [key: string]: SingleEventStatistic
}

export interface Records {
  bestGame: Record
  worstGame: Record
  oldestGamer: Record
  youngestGamer: Record
  oldestWinner: Record
  youngestWinner: Record
  bestNewcomer: Record
  bestTournament: Record
  worstTournament: Record
  mostTournamentsWon: Record
  highestWinStreak: Record // Längste Gewinnsträhne
  longestLosingStreak: Record // Längste Verlustserie
  mostHandGamesWon: Record // Meiste gewonnene Handspiele
  mostGrandGamesWon: Record // Meiste gewonnene Grands
  highestSingleSession: Record // Höchste Punktzahl in einer Session
  mostConsecutiveTournamentWins: Record // Meiste Turniersiege in Folge
  mostSecondPlaces: Record // Häufigster Zweiter
  mostTopThree: Record // Häufigste Top-3-Platzierungen
  biggestComeback: Record // Größte Aufholjagd (Differenz Session 1 zu 2)
  biggestLead: Record // Größter Vorsprung nach Session 1
  mostDifferentOpponents: Record // Gegen die meisten verschiedenen Spieler gespielt
  mostPlayedWith: Record // Am häufigsten mit einem Spieler am Tisch
  perfectTournament: Record // Bestes Turnier ohne verlorenes Spiel
  mostImprovedPlayer: Record // Größte Verbesserung zwischen zwei Turnieren
  mostConsistentPlayer: Record // Geringste Abweichung in Platzierungen
}

export interface Opponent {
  [key: string]: number // string === memberId
}

export interface MemberOpponent {
  memberId: string
  opponents: Opponent
}

export enum Entity {
  members = 'members',
  relations = 'relations',
  events = 'events',
  users = 'users',
  eventMembers = 'eventMembers',
  eventPriceCategories = 'eventPriceCategories',
  eventLocations = 'eventLocations',
  eventResults = 'eventResults',
}

export enum Method {
  POST = 'POST',
  GET = 'GET',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export interface MemberResponse {
  'count': number
  'results': Member[]
}

export interface EventResponse {
  'count': number
  'results': Event[]
}

export interface EventLocationResponse {
  'count': number
  'results': EventLocation[]
}

export interface EventMemberResponse {
  'count': number
  'results': EventMember[]
}

export interface RelationResponse {
  count: number
  results: FamilyNode[]
}

export interface GameEnumStatistics {
  [key: string]: number
}

export interface GamePropertyStatistics {
  [key: string]: number
}

export interface GameStatistics {
  totalGames: number
  byType: GameEnumStatistics
  byProperty: GamePropertyStatistics
  handGames: number
  wonGames: number
  mitGames: number
  ohneGames: number
  averageValue: number
  totalValue: number
}

export interface LocationStatistic {
  locationId?: string
  eventCount: number
  totalPlayers: number
  averagePlayersPerEvent: number
}

export interface OrganizerStatistic {
  memberId: string
  eventCount: number
  firstEvent: number
  lastEvent: number
}

export interface PlayerParticipation {
  memberId: string
  participationCount: number
  firstParticipation: number
  lastParticipation: number
  averagePlayersWhenParticipating: number
}

export interface EventOverallStatistics {
  totalEvents: number
  locationStats: LocationStatistic[]
  organizerStats: OrganizerStatistic[]
  playerParticipations: PlayerParticipation[]
  averagePlayersPerEvent: number
}

export interface StatisticResponse {
  eternalList: EternalMemberPoints[]
  participation: Participation[]
  records: Records
  memberStatistics: MemberStatistic
  eventStatistic: EventStatistic
  memberOpponents: MemberOpponent[]
  stars: StarStatistic
  globalGameStatistics: GameStatistics
  eventOverallStatistics: EventOverallStatistics
}

export interface SingleEventResultResponse {
  'count': number
  'results': EventResult
}

export interface EventPriceCategoryResponse {
  'count': number
  'results': EventPriceCategory[]
}

export interface FetchResponse {
  'count': number
  'next': 'string'
  'previous': 'string'
  'results': any[]
}

export interface FetchError {
  message: 'string'
}
